import * as yup from 'yup';

import {
  AutomatedCampaignTypeEnum,
  AutomatedConnectionTypeEnum,
  AutomationStatusEnum,
  ICreateAutomatedCampaignFormValues,
  ICreateAutomatedConnectionFormValues,
} from '../types/automations';
import { ISelectorValue } from '../types/shell';
import {
  ANNIVERSARY_YEAR,
  CAMPAIGN_IDS,
  CONNECTION_ID,
  NAME,
  SENDER_ID,
  STATUS,
  TOKEN,
  TYPE,
  UID,
} from './automationKeys';

export * from './automationKeys';

export const AUTOMATION_FORM_FIELDS_MAP: { [k: string]: string } = {
  [NAME]: 'Name',
  [TOKEN]: 'API key',
  [TYPE]: 'Type',
  [CONNECTION_ID]: 'Connection',
  [SENDER_ID]: 'Sender',
  [CAMPAIGN_IDS]: 'Campaigns',
  [ANNIVERSARY_YEAR]: 'Anniversary year',
};

export const AUTOMATED_CONNECTION_ADDED_TEXT = 'New automated connection was added';
export const AUTOMATED_CONNECTION_SAVE_ERROR_TEXT = "Something bad happened. Connection wasn't saved.";
export const AUTOMATED_CONNECTION_DELETED_TEXT = 'Connection was deleted';
export const AUTOMATED_CONNECTION_DELETE_ERROR_TEXT = "Something bad happened. Connection wasn't deleted.";
export const AUTOMATED_CONNECTION_TEST_SUCCESS_TEXT = 'Connection test passed';
export const AUTOMATED_CONNECTION_TEST_ERROR_TEXT = 'Connection test failed.';

export const AUTOMATED_CAMPAIGNS_VALIDATION_SCHEMA = yup.object().shape({
  [NAME]: yup.string().label(AUTOMATION_FORM_FIELDS_MAP[NAME]).required(),
  [UID]: yup.string(),
  [TYPE]: yup.string().label(AUTOMATION_FORM_FIELDS_MAP[TYPE]).required(),
  [ANNIVERSARY_YEAR]: yup.number().when(TYPE, {
    is: AutomatedCampaignTypeEnum.Anniversary,
    then: (sc) => sc.label(AUTOMATION_FORM_FIELDS_MAP[ANNIVERSARY_YEAR]).required(),
    otherwise: (sc) => sc.label(AUTOMATION_FORM_FIELDS_MAP[ANNIVERSARY_YEAR]),
  }),
  [CONNECTION_ID]: yup.string().label(AUTOMATION_FORM_FIELDS_MAP[CONNECTION_ID]).required(),
  [SENDER_ID]: yup.string().label(AUTOMATION_FORM_FIELDS_MAP[SENDER_ID]).required(),
  [CAMPAIGN_IDS]: yup.array().of(yup.string()).label(AUTOMATION_FORM_FIELDS_MAP[CAMPAIGN_IDS]).required(),
});

export const ANNIVERSARY_YEAR_MIN = 1;

export const AUTOMATED_CAMPAIGN_CREATE_VALIDATION_SCHEMA = yup.object().shape({
  [NAME]: yup.string().trim().required('Enter a name'),
  [TYPE]: yup.string().required('Select a type'),
  [ANNIVERSARY_YEAR]: yup.mixed().when(TYPE, {
    is: AutomatedCampaignTypeEnum.Anniversary,
    then: () =>
      yup
        .number()
        .typeError('Enter anniversary year')
        .required('Enter anniversary year')
        .min(ANNIVERSARY_YEAR_MIN, `Anniversary year must be at least ${ANNIVERSARY_YEAR_MIN}`),
    otherwise: () => yup.mixed().notRequired(),
  }),
  [CONNECTION_ID]: yup.string().required('Select a connection'),
  [SENDER_ID]: yup.string().required('Select a sender'),
  [CAMPAIGN_IDS]: yup
    .array()
    .of(yup.string())
    .min(1, 'Add at least one campaign')
    .required('Add at least one campaign'),
});

export const AUTOMATED_CAMPAIGN_CREATE_INITIAL_VALUES: ICreateAutomatedCampaignFormValues = {
  [NAME]: '',
  [TYPE]: '',
  [ANNIVERSARY_YEAR]: ANNIVERSARY_YEAR_MIN,
  [CONNECTION_ID]: '',
  [SENDER_ID]: '',
  [CAMPAIGN_IDS]: [],
};

export const AUTOMATED_CAMPAIGN_CREATE_TITLE = 'Create automated campaign';
export const AUTOMATED_CAMPAIGN_ADDED_TEXT = 'New automated campaign was added';
export const AUTOMATED_CAMPAIGN_SAVE_ERROR_TEXT = 'Something bad happened. Please, try again';
export const AUTOMATED_CAMPAIGN_ADD_CAMPAIGN_PLACEHOLDER = 'Add campaign';

export const AUTOMATED_CAMPAIGN_FIELD_LABELS = {
  [NAME]: 'Name',
  [TYPE]: 'Type',
  [ANNIVERSARY_YEAR]: 'Anniversary year',
  [CONNECTION_ID]: 'Connection',
  [SENDER_ID]: 'Sender',
  [CAMPAIGN_IDS]: 'Campaigns',
};

export const AUTOMATED_CAMPAIGN_FIELD_HELPERS = {
  [ANNIVERSARY_YEAR]: 'Years of service, for example 5',
  [CONNECTION_ID]: 'Employee data comes from this connection',
  [SENDER_ID]: "Sends go out under this user's name",
  [CAMPAIGN_IDS]: 'Sent each time this automation runs',
};

export const AUTOMATED_CAMPAIGN_EDIT_VALIDATION_SCHEMA = yup.object().shape({
  [NAME]: yup.string().trim().required('Enter a name'),
  [TYPE]: yup.string().required('Select a type'),
  [CONNECTION_ID]: yup.string().required('Select a connection'),
  [SENDER_ID]: yup.string().required('Select a sender'),
  [CAMPAIGN_IDS]: yup
    .array()
    .of(yup.string())
    .min(1, 'Add at least one campaign')
    .required('Add at least one campaign'),
});

export const AUTOMATED_CAMPAIGN_EDIT_TITLE = 'Edit automated campaign';
export const AUTOMATED_CAMPAIGN_YEAR_LOCKED_HELPER = "Can't be changed after the campaign is created";
export const AUTOMATED_CAMPAIGN_DEACTIVATE_TITLE = 'Temporarily deactivate';
export const AUTOMATED_CAMPAIGN_DEACTIVATE_DESCRIPTION = 'Pauses this campaign until you turn this off';
export const AUTOMATED_CAMPAIGN_DELETED_TEXT = 'Automated campaign was deleted';
export const AUTOMATED_CAMPAIGN_DELETE_ERROR_TEXT = "Something bad happened. Campaign wasn't deleted.";
export const AUTOMATED_CAMPAIGN_SAVED_INDICATOR_MS = 2000;

export const AUTOMATED_CONNECTION_CREATE_VALIDATION_SCHEMA = yup.object().shape({
  [NAME]: yup.string().trim().required('Enter a name'),
  [TYPE]: yup.string().required('Select a type'),
  [TOKEN]: yup.string().trim().required('Enter the API key'),
});

export const AUTOMATED_CONNECTION_CREATE_INITIAL_VALUES: ICreateAutomatedConnectionFormValues = {
  [NAME]: '',
  [TYPE]: '',
  [TOKEN]: '',
  [STATUS]: AutomationStatusEnum.Active,
};

export const AUTOMATED_CONNECTION_CREATE_TITLE = 'Add connection';

export const AUTOMATED_CONNECTION_FIELD_LABELS = {
  [NAME]: 'Name',
  [TYPE]: 'Type',
  [TOKEN]: 'API key',
};

export const AUTOMATED_CONNECTION_API_KEY_HELPER = 'Copy this key from your Merge.dev account';

export const AUTOMATED_CONNECTION_EDIT_TITLE = 'Edit connection';
export const AUTOMATED_CONNECTION_API_KEY_REPLACE_HELPER = 'Enter a new key to replace the saved one';
export const AUTOMATED_CONNECTION_API_KEY_SAVE_FIRST_HELPER = 'Save the new key, then Test';
export const AUTOMATED_CONNECTION_API_KEY_FINISH_ERROR = 'Save or cancel the new key to finish';
export const AUTOMATED_CONNECTION_API_KEY_REJECTED_ERROR = 'Merge.dev rejected this key. Check it and save again.';
export const AUTOMATED_CONNECTION_API_KEY_PLACEHOLDER = '••••••••••••••••';
export const AUTOMATED_CONNECTION_DEACTIVATE_DESCRIPTION =
  'Pauses this connection and every campaign that uses it. Scheduled sends still go out.';
export const AUTOMATED_CONNECTION_SAVED_INDICATOR_MS = 2000;

export const AUTOMATED_CAMPAIGNS_INITIAL_VALUES = {
  [NAME]: '',
  [TYPE]: undefined,
  [ANNIVERSARY_YEAR]: 1,
  [CONNECTION_ID]: undefined,
  [SENDER_ID]: undefined,
  [CAMPAIGN_IDS]: [],
  [STATUS]: AutomationStatusEnum.Active,
};

export const CONNECTION_TYPES_SELECT_OPTIONS: ISelectorValue[] = [
  {
    label: 'HR',
    value: AutomatedConnectionTypeEnum.HR,
  },
  {
    label: 'Recruiting (coming soon)',
    value: AutomatedConnectionTypeEnum.Recruiting,
    disabled: true,
  },
];

export const CAMPAIGN_TYPES_SELECT_OPTIONS: ISelectorValue[] = [
  {
    label: 'New hire',
    value: AutomatedCampaignTypeEnum.NewHire,
  },
  {
    label: 'Birthday',
    value: AutomatedCampaignTypeEnum.Birthday,
  },
  {
    label: 'Anniversary',
    value: AutomatedCampaignTypeEnum.Anniversary,
  },
];

export const CAMPAIGN_DEACTIVATE_TOOLTIP_TEXT =
  'This will temporarily deactivate the automated campaign for as long as it is turned on. Turn off to resume the automated campaign.';

export const CAMPAIGNS_TOOLTIP_TEXT = 'Select one or more campaigns to send for this type of automated campaign.';

export const CONNECTION_TOOLTIP_TEXT = 'Choose a connection that should be used to get employee information.';

export const SENDER_TOOLTIP_TEXT = 'Choose a user to be the campaign sender.';

export const ANNIVERSARY_YEAR_TOOLTIP_TEXT =
  'Anniversary year is available when the corresponding campaign type is selected.';

export const AUTOMATED_CONNECTION_DELETE_TITLE = 'Delete connection?';
export const AUTOMATED_CONNECTION_DELETE_MESSAGE =
  'Campaigns that use this connection will be deactivated. Scheduled sends still go out.';

export const AUTOMATED_CAMPAIGN_DELETE_TITLE = 'Delete automated campaign?';
export const AUTOMATED_CAMPAIGN_DELETE_MESSAGE =
  'To keep sending automatically, create a new campaign. Scheduled sends still go out.';
